import type {PlaceId, PluginContext, PluginInterface} from '@jetbrains/teamcity-ui'
import {ContentPanelContext} from '@jetbrains/teamcity-ui/dist/components'
import {isValidPluginReactElementType} from '@jetbrains/teamcity-ui/dist/plugin'
import pluginRegistry from '@jetbrains/teamcity-ui/dist/plugin/registry'
import classNames from 'classnames'
import * as React from 'react'
import {useSyncExternalStore} from 'react'
import type {$PropertyType} from 'utility-types'

import {useContentPanelContextValue} from '../../ContentPanel/ContentPanel.hooks'
import styles from '../Plugin.module.css'

import PluginErrorBoundary from './SakuraUIPlugin.errorBoundary'

type PluginLocationContext = $PropertyType<PluginContext, 'location'>
type OwnProps = {
  name: string
  placeId: PlaceId
  activeEntities: PluginLocationContext
}

declare global {
  interface HTMLDivElement {
    _teamCityPlugin?: PluginInterface
  }
}

const SakuraUIPlugin = ({name, activeEntities, placeId}: OwnProps) => {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const plugin: PluginInterface | null | undefined = useSyncExternalStore(
    onStoreChange => pluginRegistry.subscribe(onStoreChange),
    () => pluginRegistry.findUniquePlugin(placeId, name),
  )
  const contentPanelContextValue = useContentPanelContextValue()
  React.useEffect(() => {
    if (plugin != null) {
      plugin.updateContext({
        location: activeEntities,
        contentPanel: contentPanelContextValue,
      })
    }
  }, [activeEntities, contentPanelContextValue, plugin])
  React.useEffect(() => {
    if (containerRef.current != null && plugin != null) {
      containerRef.current.dataset.teamCityPluginName = plugin.name
      containerRef.current._teamCityPlugin = plugin
    }
  }, [plugin])
  const content = plugin?.content ?? ''
  const isInternalReactPlugin = isValidPluginReactElementType(content) && plugin?.options._internal
  React.useEffect(() => {
    if (plugin == null) {
      return undefined
    }

    if (containerRef.current != null || isInternalReactPlugin) {
      if (!isInternalReactPlugin) {
        if (plugin.container) {
          containerRef.current?.appendChild(plugin.container)
        }
      }

      plugin.mount()
    }

    return () => plugin.unmount()
  }, [isInternalReactPlugin, plugin])
  const containerClassNames = plugin?.options.containerClassNames
  return React.useMemo(() => {
    const className = classNames(styles.container, containerClassNames)

    if (isInternalReactPlugin) {
      const component = React.createElement(content, {
        location: activeEntities,
      })
      return (
        <ContentPanelContext value={contentPanelContextValue}>
          <div className={className} ref={containerRef}>
            {component}
          </div>
        </ContentPanelContext>
      )
    } else {
      return <div className={className} ref={containerRef} />
    }
  }, [
    containerClassNames,
    content,
    activeEntities,
    contentPanelContextValue,
    isInternalReactPlugin,
  ])
}

function Plugin(props: OwnProps) {
  return (
    <PluginErrorBoundary name={props.name} placeId={props.placeId}>
      <SakuraUIPlugin {...props} />
    </PluginErrorBoundary>
  )
}

export default Plugin
